import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React, {useCallback, useContext, useMemo} from 'react';

import {trackCreateReportStarted} from '../../../services/analytics';
import {isInJupyterNotebook} from '../../../setup';
import {useRampFlagCoreweaveIssues} from '../../../state/issues/useCoreweaveIssues';
import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {usePanelBankSectionsPagingContext} from '../../../state/panelbank/PanelBankSectionsPagingContext';
import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {usePanelBankConfig} from '../../../state/views/panelBankConfig/hooks';
import * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import {panelIsActive} from '../../../util/panelbank';
import {getFlowSectionsWithVisiblePanels} from '../../../util/panelbankFlow';
import {LayedOutPanel} from '../../../util/panelTypes';
import {
  useStatsigGateRunDetailsTabbed,
  useStatsigGateWorkspaceConsolidation,
} from '../../../util/rampFeatureFlags';
import * as RunHelpers from '../../../util/runhelpers';
import {CreateReportModal} from '../../CreateReportModal';
import {useWorkspaceSettingsForReportCreation} from '../../CreateReportModal/useReportSettings';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {WorkspaceSettings} from '../../SettingControls/WorkspaceSettings';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {INLINE_DRAWER_MIN_PANEL_BANK_WIDTH} from '../../WorkspaceDrawer/constants';
import {RunIssuesButton} from '../../WorkspaceDrawer/RunIssues/RunIssuesButton';
import {usePanelBankWidthContext} from '../contextProviders/PanelBankWidthContext';
import {PanelSearchInput} from '../search/PanelSearchInput';
import {SearchAutoExpandToggle} from '../search/SearchAutoExpandToggle';
import {SearchResultCounts} from '../search/SearchResultCounts';
import {PanelBankSectionConfig} from '../types';
import {AddPanelsDrawerButton} from './AddPanelsDrawerButton';
import {PanelBankControlsOverflowMenu} from './PanelBankControlsOverflowMenu';
import {PanelBankCreateReportButton} from './PanelBankCreateReportButton';
import {SingleRunHeader} from './SingleRunHeader';
interface PanelBankControlsProps {
  entityName: string;
  projectName: string;
  isProjectReadOnly?: boolean;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  workspaceID?: string;
}

// This is the bar at the top of the PanelBank that contains search and settings
export const PanelBankControls = (props: PanelBankControlsProps) => {
  const {
    entityName,
    panelBankConfigRef,
    projectName,
    isProjectReadOnly,
    workspaceID,
  } = props;

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const {isSingleRun} = useSingleRunModeContext();

  const isIssuesObservabilityEnabled = useRampFlagCoreweaveIssues();

  const {currentPageBySectionRefID} = usePanelBankSectionsPagingContext();
  const {setExportingUnknownID} = useContext(PanelBankUpdaterContext);
  const setExporting = useCallback(
    () => setExportingUnknownID(true),
    [setExportingUnknownID]
  );

  const {debouncedSearchRegex: searchRegex, isSearching} =
    usePanelSearchDebouncedContext();

  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo.keys);

  const {panelBankWidth} = usePanelBankWidthContext();
  const useExportableSections = () => {
    const {sections} = usePanelBankConfig(panelBankConfigRef);
    return useMemo(() => {
      return getFlowSectionsWithVisiblePanels({
        sections,
        currentPageBySectionRefID,
        isSingleRun,
        searchRegex,
        historyKeyInfo,
        keyTypes,
        panelBankWidth,
      });
    }, [sections]);
  };

  /**
   * Note(jofang): Purposefully leaving this commented out code in while I
   * wait for product and design to decide on the direction of this use case.
   */
  // const pushDraftReport = usePushReport(entityName, projectName);

  const usePanelIsActive = useCallback(
    (section: PanelBankSectionConfig, panel: LayedOutPanel) =>
      panelIsActive({
        section,
        panel,
        isSingleRun,
        searchRegex,
        historyKeyInfo,
        keyTypes,
      }),
    [isSingleRun, searchRegex, historyKeyInfo, keyTypes]
  );

  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const inheritedSettings =
    useWorkspaceSettingsForReportCreation(workspaceSettingsRef);
  const isRunDetailsTabbedEnabled = useStatsigGateRunDetailsTabbed();

  const shouldDisplayIconOnly =
    panelBankWidth <= INLINE_DRAWER_MIN_PANEL_BANK_WIDTH;

  return (
    <TailwindContents>
      {isSingleRun &&
        isWorkspaceConsolidationEnabled &&
        !isRunDetailsTabbedEnabled && <SingleRunHeader />}
      <div
        data-test="panel-bank-controls"
        className={classNames(
          'p-16 border-b border-b-moon-250 hidden md:block',
          {
            'flex flex-1 min-w-0 w-full': isRunDetailsTabbedEnabled,
          }
        )}>
        <div className="flex gap-x-8 gap-y-16">
          <PanelSearchInput />
          <div className="hidden gap-x-8 md:flex">
            <PanelBankControlsOverflowMenu />
            {workspaceID != null && (
              <WorkspaceSettings isIconOnly={shouldDisplayIconOnly} />
            )}
            <div className="flex gap-8">
              {!isProjectReadOnly && (
                <>
                  <div>
                    {!isInJupyterNotebook() && (
                      <PanelBankCreateReportButton
                        isIconOnly={shouldDisplayIconOnly}
                        disabled={false}
                        entityName={entityName}
                        projectName={projectName}
                        onClick={() => {
                          trackCreateReportStarted({
                            location: `workspace create report button`,
                            entityName,
                            projectName,
                          });

                          setExporting();
                        }}
                      />
                    )}
                  </div>
                  {isIssuesObservabilityEnabled && isSingleRun && (
                    <RunIssuesButton />
                  )}
                  <CreateReportModal
                    inheritedSettings={inheritedSettings}
                    useExportableSections={useExportableSections}
                    panelIsActive={usePanelIsActive}
                  />
                </>
              )}

              <AddPanelsDrawerButton />
            </div>
          </div>
        </div>
        {isSearching && (
          <div className="mt-16 flex items-start gap-16">
            <SearchResultCounts />
            <SearchAutoExpandToggle />
          </div>
        )}
      </div>
    </TailwindContents>
  );
};
